// ============================================================
// オフ会予算カード（v1.12.0・2026-08-05 社長指示）
//
// 「旅行実費記録表とタスクアプリで同じ数字になるように表示・管理したい」への答え。
// 数字は一切アプリ側で計算せず、スプシ「旅行費用管理」事業タブの集計ブロックを
// /api/meetup-budget から取得してそのまま表示する＝出どころが1つなのでズレようがない。
//
// 対象は **事業区分のみ**（社長決定）。月間・年間それぞれの合計を出すだけで、
// 大型遠征枠との自動区分はしない／繰越もしない。
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { formatYen } from '../utils/tripLogic';
import { readBudgetResponse, validateBudgetSummary } from '../utils/meetupBudgetApi';

const API = '/api/meetup-budget';

// compact=true … 旅行の詳細設定中に出す1行版（プランの邪魔をしない）。タップで展開できる
// compact=false … プランを立てているときに出す大きい版（予算の残りを見ながら組める）
export default function MeetupBudgetCard({ compact = false, contextDate = null }) {
  const [data, setData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [error, setError] = useState('');
  const [needsPageReload, setNeedsPageReload] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState({ monthly: '', annual: '' });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setNeedsPageReload(false);
    try {
      const res = await fetch(API, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      const body = validateBudgetSummary(await readBudgetResponse(res));
      setData(body);
      setForm({ monthly: String(body.monthly.budget), annual: String(body.annual.budget) });
    } catch (e) {
      setNeedsPageReload(Boolean(e.needsPageReload) || e instanceof TypeError);
      setError(e instanceof TypeError ? '通信できませんでした。ネット接続を確認し、「接続を確認」を押してください。' : e.message || '通信に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let timer;
    const refresh = () => { window.clearTimeout(timer); timer = window.setTimeout(load, 500); };
    window.addEventListener('taskflow:budget-updated', refresh);
    return () => { window.clearTimeout(timer); window.removeEventListener('taskflow:budget-updated', refresh); };
  }, [load]);

  useEffect(() => {
    if (data && contextDate?.startsWith(String(data.year))) setSelectedMonth(Number(contextDate.slice(5, 7)));
  }, [contextDate, data?.year]);

  const save = async () => {
    const monthlyBudget = Number(form.monthly);
    const annualBudget = Number(form.annual);
    if (!Number.isInteger(monthlyBudget) || monthlyBudget < 0
      || !Number.isInteger(annualBudget) || annualBudget < 0) {
      setError('予算は0以上の整数で入力してください');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ monthlyBudget, annualBudget }),
      });
      await readBudgetResponse(res);
      setEditing(false);
      await load();
    } catch (e) {
      setNeedsPageReload(Boolean(e.needsPageReload));
      setError(e.message || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  // root は認証対象外。予算 API と同じ保護パスをトップレベルで開く。
  // Access 認証後、API が固定の root へ戻す。書き込みは再送しない。
  const reconnect = () => window.location.assign(`${API}?reconnect=1`);

  if (loading && !data) {
    return (
      <div className={`mb-card mb-card-quiet${compact ? ' mb-compact' : ''}`}>
        オフ会予算を読み込み中…
      </div>
    );
  }
  if (!data) {
    return (
      <div className={`mb-card mb-card-quiet${compact ? ' mb-compact' : ''}`}>
        <span>オフ会予算を取得できませんでした{error ? `（${error}）` : ''}</span>
        <button type="button" className="mb-link" onClick={needsPageReload ? reconnect : load}>{needsPageReload ? '接続を確認' : '再読み込み'}</button>
      </div>
    );
  }

  if (error && !editing) {
    return (
      <div className="mb-card mb-card-quiet">
        <span>オフ会予算を更新できませんでした（{error}）</span>
        <button type="button" className="mb-link" onClick={needsPageReload ? reconnect : load}>{needsPageReload ? '接続を確認' : '再読み込み'}</button>
      </div>
    );
  }

  const month = selectedMonth || data.month;
  const monthInfo = data.months?.find(info => info.month === month) || data.monthly;
  const hasPlanned = Number.isFinite(monthInfo.planned) && Number.isFinite(monthInfo.remainIncludingPlanned);
  const monthly = { ...monthInfo, remain: hasPlanned ? monthInfo.remainIncludingPlanned : monthInfo.remain };

  // 1行版：詳細設定中はここまで。タップで大きい版に開く
  const remainText = (info) =>
    info.remain < 0 ? `${formatYen(-info.remain)}超過` : `のこり${formatYen(info.remain)}`;

  if (compact && !expanded) {
    const monthOver = hasPlanned && monthly.remain < 0;
    const annualOver = data.annual.remain < 0;
    return (
      <button
        type="button"
        className="mb-compact-bar"
        onClick={() => setExpanded(true)}
        aria-label="オフ会予算の詳細を開く"
      >
        <span className="mb-compact-title">🍻 オフ会予算</span>
        <span className="mb-compact-nums">
          <span className={monthOver ? 'mb-over' : ''}>
            {month}月 {hasPlanned ? `予定込み ${remainText(monthly)}` : '予定未取得'}
          </span>
          <span className="mb-compact-sep">／</span>
          <span className={annualOver ? 'mb-over' : ''}>
            年 {remainText(data.annual)}
          </span>
        </span>
      </button>
    );
  }

  const row = (label, info, withPlans = false) => {
    const over = info.remain < 0;
    const pct = info.budget > 0
      ? Math.min(100, Math.round(((info.actual + (withPlans ? info.planned || 0 : 0)) / info.budget) * 100))
      : 0;
    return (
      <div className="mb-row" key={label}>
        <div className="mb-row-head">
          <span className="mb-row-label">{label}</span>
          <span className={`mb-row-remain${over ? ' mb-over' : ''}`}>
            {over ? `${formatYen(-info.remain)} 超過` : `のこり ${formatYen(info.remain)}`}
          </span>
        </div>
        <div className="mb-bar">
          <div className={`mb-bar-fill${over ? ' mb-over-fill' : ''}`} style={{ width: `${pct}%` }} />
        </div>
        <div className="mb-row-sub">
          {withPlans ? <>確定実績 {formatYen(info.actual)} ／ 未確定予定 {hasPlanned ? formatYen(info.planned) : '未取得'}<br />月予算 {formatYen(info.budget)}</> : <>確定実績 {formatYen(info.actual)} / 年予算 {formatYen(info.budget)}</>}
        </div>
      </div>
    );
  };

  return (
    <div className="mb-card">
      {/* 開くときと同じく、閉じるときも上部の帯そのものを押せばよい（v1.12.3 社長指示）。
          開いている間は色を反転させて「今開いている」ことを見た目で分かるようにする。 */}
      {compact ? (
        <button
          type="button"
          className="mb-head mb-head-toggle app-toggle is-open"
          onClick={() => { setEditing(false); setExpanded(false); }}
          aria-expanded
        >
          <span className="mb-title">🍻 オフ会予算（事業）</span>
          <span className="app-toggle-caret" aria-hidden="true">▲</span>
        </button>
      ) : (
        <div className="mb-head">
          <span className="mb-title">🍻 オフ会予算（事業）</span>
        </div>
      )}

      <label className="mb-field">
        <span>{data.year}年の月を選ぶ</span>
        <select aria-label="オフ会予算の対象月" value={month} onChange={event => setSelectedMonth(Number(event.target.value))}>
          {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}月</option>)}
        </select>
      </label>
      {hasPlanned ? row(`${month}月・予定込み`, monthly, true) : <div className="mb-error">未確定予定を取得できていません。再読み込みしてください。<button type="button" onClick={load}>再読み込み</button></div>}
      {row(`今年（${data.year}年）・確定実績のみ`, data.annual)}

      <button
        type="button"
        className={`mb-edit-toggle app-toggle${editing ? ' is-open' : ''}`}
        onClick={() => setEditing((v) => !v)}
        aria-expanded={editing}
      >
        <span>予算を編集</span>
        <span className="app-toggle-caret" aria-hidden="true">{editing ? '▲' : '▼'}</span>
      </button>

      {editing && (
        <div className="mb-edit">
          <label className="mb-field">
            <span>月間予算</span>
            <input
              type="number"
              inputMode="numeric"
              value={form.monthly}
              onChange={(e) => setForm((f) => ({ ...f, monthly: e.target.value }))}
            />
          </label>
          <label className="mb-field">
            <span>年間予算</span>
            <input
              type="number"
              inputMode="numeric"
              value={form.annual}
              onChange={(e) => setForm((f) => ({ ...f, annual: e.target.value }))}
            />
          </label>
          <p className="mb-note">
            月間 × 12 と年間の差額は大型遠征代です。ここで変えるとスプレッドシートにも反映されます。
          </p>
          <button type="button" className="mb-save" onClick={save} disabled={saving}>
            {saving ? '保存中…' : '保存する'}
          </button>
        </div>
      )}

      {error && <div className="mb-error">{error}{needsPageReload && <button type="button" className="mb-link" onClick={reconnect}>接続を確認</button>}</div>}
    </div>
  );
}
